import { compact, defaultsDeep, get } from 'lodash';

import {
  DetailsSubFeatures,
  Partners,
  PAYMENT_TYPE,
  PortalConfigDefinition,
  PortalFormConfig,
} from '@lockton-apps/shared/interfaces';
import { NorwayProductConfigId } from './product-config';
import { isExperimental } from '@lockton-apps/shared/util/helpers';

const PORTAL_CONFIG: PortalConfigDefinition = {
  [Partners.DEFAULT]: {
    id: Partners.DEFAULT,
    name: 'Default',
    detailsSubFeatures: [
      DetailsSubFeatures.Overview,
      DetailsSubFeatures.CommunicationPreferences,
    ],
    other: {
      disableManageContactAsClient: false,
    },
  },
  [Partners.LSOS]: {
    id: Partners.LSOS,
    name: 'Lsos',
    navigation: {
      primaryContact: compact([
        { id: 'home' },
        { id: 'my-policies' },
        {
          id: 'resourceCentre',
          type: 'link',
          label: $localize`Resource centre`,
          href: 'https://global.lockton.com/scot/en/resources/search',
          target: '_blank',
        },
        // EXPERIMENTAL:STORY:LCP-511
        isExperimental() && { id: 'faqs' },
      ]),
      footer: compact([
        // EXPERIMENTAL:STORY:LCP-414
        isExperimental()
          ? { id: 'privacy' }
          : {
              id: 'privacy',
              href: 'https://www.locktonlaw.scot/compliance/privacy-notice.html',
            },
        { id: 'cookies' },
        {
          id: 'terms',
        },
        // EXPERIMENTAL:STORY:LCP-511
        isExperimental() && { id: 'faqs' },
        { id: 'mainSite' },
      ]),
    },
    other: {
      logoFileName: 'lsos.svg',
      showProductIcon: false,
      enableMyLocktonTeam: true,
      disableWelcomeAddressee: true,
      enableManageEnquiries: true,
      isManageRenewalsEnabled: true,
    },
    detailsSubFeatures: [
      DetailsSubFeatures.Overview,
      DetailsSubFeatures.PracticeDetails,
      DetailsSubFeatures.Principals,
      DetailsSubFeatures.CommunicationPreferences,
    ],
    paymentTypes: [
      // EXPERIMENTAL:EPIC:LCP-349
      isExperimental() ? PAYMENT_TYPE.Stripe : PAYMENT_TYPE.Offline,
      PAYMENT_TYPE.Invoice,
      PAYMENT_TYPE.PremiumFinance,
    ],
    offlinePaymentPhoneNumber: '0131 345 5599',
  },
  [Partners.PEOPLE_SOLUTIONS]: {
    id: Partners.PEOPLE_SOLUTIONS,
    name: 'People Solutions',
    formConfig: {
      policyForm: {
        fields: {
          riskReference: {
            label: $localize`Policy Number`,
          },
          riskCountry: {
            defaultValue: 'United Kingdom',
          },
        },
      },
    },
    labels: {
      policyRiskReference: $localize`Policy Number`,
      brokers: $localize`Consultants`,
      broker: $localize`Consultant`,
    },
    other: {
      policyUsesClientBrokers: true,
      logoFileName: '',
      enableMyLocktonTeam: false,
      showProductIcon: true,
      hideBrokerExpertise: true,
      defaultDocumentUploadModel: {
        deletionDays: 30,
      },
      usesLegacyQuotesApi: true,
      hideClientEnquiryNotifications: true,
    },
    detailsSubFeatures: [
      DetailsSubFeatures.Overview,
      DetailsSubFeatures.CommunicationPreferences,
    ],
  },
  [Partners.NORWAY]: {
    id: Partners.NORWAY,
    name: 'Norway',
    formConfig: {
      createAccount: {
        fields: {
          locktonId: {
            label: $localize`Company Number`,
          },
          singleClientId: {
            hidden: true,
          },
        },
        parsers: [
          (model: any) => ({
            ...model,
            singleClientId: model.locktonId,
          }),
        ],
      },
      editClient: {
        fields: {
          singleClientId: {
            hidden: true,
          },
        },
        parsers: [
          (model: any) => ({
            ...model,
            singleClientId: model.locktonId,
          }),
        ],
      },
    },
    navigation: {
      primaryContact: compact([
        { id: 'home' },
        { id: 'my-policies' },
        { id: 'my-lockton-team' },
        // EXPERIMENTAL:STORY:LCP-511
        isExperimental() && { id: 'faqs' },
      ]),
      footer: compact([
        // EXPERIMENTAL:STORY:LCP-414
        isExperimental()
          ? { id: 'privacy' }
          : {
              id: 'privacy',
              href: 'https://global.lockton.com/no/en/privacy-notice-lockton-companies-as-norway',
            },
        { id: 'cookies' },
        { id: 'terms' },
        // EXPERIMENTAL:STORY:LCP-511
        isExperimental() && { id: 'faqs' },
        { id: 'mainSite' },
      ]),
    },
    other: {
      logoFileName: 'advokat_foreningen.svg',
      showProductIcon: true,
      hideBrokerExpertise: true,
      enableMyLocktonTeam: true,
      enableManageEnquiries: true,
      isManageRenewalsEnabled: true,
      isManageNewBusinessEnabled: true,
      disableWelcomeAddressee: true,
      usesLegacyQuotesApi: true,
      hideClientEnquiryNotifications: false,
    },
    currencyConfig: {
      currencyCode: 'NOK',
      digitsInfo: '1.2-2',
      locale: 'nb',
      mask: {
        mask: 'separator',
        config: { thousandSeparator: ',' },
      },
    },
    productOverrides: {
      [NorwayProductConfigId.Default]: {
        name: 'Norway Professional Indemnity',
      },
    },
    detailsSubFeatures: [
      DetailsSubFeatures.Overview,
      DetailsSubFeatures.CommunicationPreferences,
    ],
  },
  [Partners.ACCA]: {
    id: Partners.ACCA,
    name: 'ACCA',
    currencyConfig: {
      currencyCode: 'GBP',
      digitsInfo: '1.2',
      locale: 'en',
    },
    other: {
      enableManageEnquiries: true,
      isManageRenewalsEnabled: true,
      isManageNewBusinessEnabled: true,
      usesLegacyQuotesApi: false,
      showProductIcon: true,
      isRiskReferenceReadonly: true,
    },
    detailsSubFeatures: [
      DetailsSubFeatures.Overview,
      DetailsSubFeatures.CommunicationPreferences,
    ],
    paymentTypes: [
      // EXPERIMENTAL:EPIC:LCP-349
      isExperimental() ? PAYMENT_TYPE.Stripe : PAYMENT_TYPE.Offline,
      PAYMENT_TYPE.Invoice,
      PAYMENT_TYPE.PremiumFinance,
    ],
    offlinePaymentPhoneNumber: '01273 223309',
  },
  [Partners.PNC]: {
    id: Partners.PNC,
    name: 'P&C',
    other: {
      enableManageEnquiries: true,
      isManageRenewalsEnabled: true,
      isManageNewBusinessEnabled: true,
      isProposalDocumentsEnabled: true,
      usesLegacyQuotesApi: true,
      hideClientEnquiryNotifications: true,
    },
    detailsSubFeatures: [
      DetailsSubFeatures.Overview,
      DetailsSubFeatures.CommunicationPreferences,
    ],
  },
  [Partners.CONSTRUCTION]: {
    id: Partners.CONSTRUCTION,
    name: 'Lockton Construction',
    other: {
      enableManageEnquiries: true,
      isManageRenewalsEnabled: true,
      isManageNewBusinessEnabled: true,
    },
    detailsSubFeatures: [
      DetailsSubFeatures.Overview,
      DetailsSubFeatures.CommunicationPreferences,
    ],
  },
  [Partners.ENW]: {
    id: Partners.ENW,
    name: 'England & Wales Solicitors',
    other: {
      enableManageEnquiries: true,
      isManageRenewalsEnabled: true,
      isManageNewBusinessEnabled: true,
    },
    navigation: {
      primaryBroker: compact([
        { id: 'home' },
        { id: 'clients' },
        { id: 'brokers' },
        {
          id: 'news-and-events-enw',
          type: 'link',
          label: $localize`News & Events`,
          href: 'https://global.lockton.com/gb/en/news-insights/search?articleTypes=Alerts,Articles,Case%20Studies,Client%20Story,Videos,Podcasts,Whitepapers&industries=Solicitors',
          target: '_blank',
        },
        {
          id: 'news-enw',
          type: 'link',
          label: $localize`Toolkit`,
          href: 'https://global.lockton.com/gb/en/solicitors-toolkit',
          target: '_blank',
        },
        // EXPERIMENTAL:STORY:LCP-511
        isExperimental() && { id: 'faqs' },
      ]),
      primaryContact: compact([
        { id: 'home' },
        { id: 'my-policies' },
        {
          id: 'news-and-events-enw',
          type: 'link',
          label: $localize`News & Events`,
          href: 'https://global.lockton.com/gb/en/news-insights/search?articleTypes=Alerts,Articles,Case%20Studies,Client%20Story,Videos,Podcasts,Whitepapers&industries=Solicitors',
          target: '_blank',
        },
        {
          id: 'news-enw',
          type: 'link',
          label: $localize`Toolkit`,
          href: 'https://global.lockton.com/gb/en/solicitors-toolkit',
          target: '_blank',
        },
        // EXPERIMENTAL:STORY:LCP-511
        isExperimental() && { id: 'faqs' },
      ]),
    },
    detailsSubFeatures: [
      DetailsSubFeatures.Overview,
      DetailsSubFeatures.CommunicationPreferences,
    ],
  },
  [Partners.REGULATORY]: {
    id: Partners.REGULATORY,
    name: 'Regulatory',
    other: {
      enableManageEnquiries: true,
      isManageRenewalsEnabled: true,
      isManageNewBusinessEnabled: true,
    },
    detailsSubFeatures: [
      DetailsSubFeatures.Overview,
      DetailsSubFeatures.CommunicationPreferences,
    ],
  },
  [Partners.ACCA_OVER]: {
    id: Partners.ACCA_OVER,
    name: 'ACCA Over',
    currencyConfig: {
      currencyCode: 'GBP',
      digitsInfo: '1.2',
      locale: 'en',
    },
    other: {
      enableManageEnquiries: true,
      isManageRenewalsEnabled: true,
      isManageNewBusinessEnabled: true,
      showProductIcon: true,
      isRiskReferenceReadonly: true,
    },
    detailsSubFeatures: [
      DetailsSubFeatures.Overview,
      DetailsSubFeatures.CommunicationPreferences,
    ],
    paymentTypes: [
      // EXPERIMENTAL:EPIC:LCP-349
      isExperimental() ? PAYMENT_TYPE.Stripe : PAYMENT_TYPE.Offline,
      PAYMENT_TYPE.Invoice,
      PAYMENT_TYPE.PremiumFinance,
    ],
    offlinePaymentPhoneNumber: '01273 223309',
  },
  [Partners.RPA]: {
    id: Partners.RPA,
    name: 'RPA',
    navigation: {
      primaryContact: [{ id: 'home' }, { id: 'faqs' }],
    },
    other: {
      disableManageContactAsClient: true,
    },
  },
};

const PORTAL_DEF_EQUIVALENTS = {
  [Partners.NORWAY_PI]: defaultsDeep(
    { id: Partners.NORWAY_PI, other: { usesLegacyQuotesApi: false } },
    PORTAL_CONFIG[Partners.NORWAY]
  ),
};

export const STATIC_PORTAL_CONFIG = {
  ...PORTAL_CONFIG,
  ...PORTAL_DEF_EQUIVALENTS,
};

export function getPortalFormConfig(
  portalId: string | number,
  formId: string,
  portalConfigDefinition = STATIC_PORTAL_CONFIG
): PortalFormConfig | undefined {
  return get(portalConfigDefinition, `${portalId}.formConfig.${formId}`);
}

export function getPortalFormParsers(
  portalId: string | number,
  formId: string
) {
  return getPortalFormConfig(portalId, formId)?.parsers;
}
